import React, { useState, useEffect, useRef, useContext, useCallback } from "react";
import "./GroceryAddCustomerModal.css";
import { Box, FormControl, Modal, CircularProgress, IconButton } from "@mui/material";
import PalletScooter from "assets/images/illustrations/pallet-scooter.png";
import AachiLogo from "assets/images/logos/aachi-logo.png";
import LockResetIcon from "@mui/icons-material/LockReset";
import Swal from "sweetalert2";
import {
  isStaleCartError,
  clearStaleCartStorage,
  showStaleCartDialog,
} from "../../../../../utils/staleCartHandler";
import { useDispatch } from "react-redux";
import { useSnackbar } from "../../hooks/SnackbarProvider";
import useAuthModal from "../../hooks/useAuthModal";
import { UserContext } from "../../../../../App";
import { countryOptions } from "../../../posnavbar/components/addCustomer/data/countries-details";
import {
  addCustomer,
  // AddedCustomerData,
  createB2CcustomerApi,
  // customerSearch,
  customerSearchV2Api,
  SkipUserFun,
  createSalesOrderCart,
  getRetailPartnerList,
  getRetailPartnerDetails,
  VerifyGST,
  createB2BCustomer,
} from "../../../../../config/Services";
import { changePrevAddedTime, eventTrackerTime } from "../redux/Actions/eventTracker";
import NewCountryCodeInput from "./NewCountryCodeInput";
import { BootstrapInput } from "../../../posnavbar/components/addCustomer/components/bootStrapInput";
import useGetDevice from "../../hooks/useGetDevice";
import useCustomerInsights, {
  formatInsightCurrency,
  formatInsightCount,
  formatInsightDate,
  InsightSpinner,
} from "../../../../../hooks/useCustomerInsights";
import moment from "moment";

// ─── Order option icons ───────────────────────────────────────────────────────
const InStoreIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="lucide lucide-store h-3.5 w-3.5"
    aria-hidden="true"
  >
    <path d="M15 21v-5a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v5" />
    <path d="M17.774 10.31a1.12 1.12 0 0 0-1.549 0 2.5 2.5 0 0 1-3.451 0 1.12 1.12 0 0 0-1.548 0 2.5 2.5 0 0 1-3.452 0 1.12 1.12 0 0 0-1.549 0 2.5 2.5 0 0 1-3.77-3.248l2.889-4.184A2 2 0 0 1 7 2h10a2 2 0 0 1 1.653.873l2.895 4.192a2.5 2.5 0 0 1-3.774 3.244" />
    <path d="M4 10.95V19a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8.05" />
  </svg>
);

const CurbsideIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="lucide lucide-bike h-3.5 w-3.5"
    aria-hidden="true"
  >
    <circle cx="18.5" cy="17.5" r="3.5" />
    <circle cx="5.5" cy="17.5" r="3.5" />
    <circle cx="15" cy="5" r="1" />
    <path d="M12 17.5V14l-3-3 4-3 2 3h2" />
  </svg>
);

const DeliveryIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="lucide lucide-map-pin h-3.5 w-3.5"
    aria-hidden="true"
  >
    <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

// ─── Order types (top-level) ──────────────────────────────────────────────────
const ORDER_TYPES = [
  { id: "RETAIL", label: "Retail" },
  { id: "GST_INVOICE", label: "GST Invoice" },
];

// ─── Order options (only shown for RETAIL type) ───────────────────────────────
const ORDER_OPTIONS = [
  { id: "IN_STORE", label: "In-store", Icon: InStoreIcon, disabled: false },
  { id: "CURBSIDE", label: "Curbside pick up", Icon: CurbsideIcon, disabled: true },
  { id: "DELIVERY", label: "Delivery", Icon: DeliveryIcon, disabled: true },
];

// ─── Order options for GST Invoice (all enabled) ─────────────────────────────
const GST_ORDER_OPTIONS = [
  { id: "IN_STORE", label: "In-store", Icon: InStoreIcon },
  { id: "CURBSIDE", label: "Curbside", Icon: CurbsideIcon },
  { id: "DELIVERY", label: "Delivery", Icon: DeliveryIcon },
];

const RECENT_GST_PARTNERS_KEY = "recentGstPartners";

/* ── Grocery tips for the mascot animation ─────────────────── */
const GROCERY_TIPS = [
  { icon: "🛒", text: "Loyalty members spend 20% more per visit on average" },
  { icon: "🍎", text: "Fresh produce is the #1 reason shoppers choose a grocery store" },
  { icon: "📊", text: "End-cap displays generate 3× more sales than mid-aisle shelves" },
  { icon: "💡", text: "Shoppers decide 70% of purchases while inside the store" },
  { icon: "📦", text: "Bundled offers lift average basket size by 20–30%" },
  { icon: "⏰", text: "Peak hours are 11 am–1 pm and 5–7 pm on weekdays" },
  { icon: "🎯", text: "Impulse purchases account for up to 60% of grocery revenue" },
  { icon: "🏷️", text: "Clear price labels reduce checkout queries by 40%" },
];

/* ── Delivery-agent transit animation (mirrors restaurant QSR modal) ── */
const GcdDeliveryMascot = () => {
  const [phase, setPhase] = useState("pre");
  const [bouncing, setBouncing] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [tipIdx, setTipIdx] = useState(0);
  const [tipVisible, setTipVisible] = useState(false);
  const [cycleKey, setCycleKey] = useState(0);

  useEffect(() => {
    setPhase("pre");
    setBouncing(false);
    const timers = [
      setTimeout(() => setPhase("driving-in"), 60),
      setTimeout(() => {
        setPhase("stopped");
        setBouncing(true);
        setTimeout(() => setBouncing(false), 520);
      }, 1060),
      setTimeout(() => {
        setShowMessage(true);
        setTipVisible(true);
      }, 1760),
      setTimeout(() => setPhase("driving-out"), 3060),
      setTimeout(() => {
        setPhase("done");
        setTimeout(() => setCycleKey((k) => k + 1), 1500);
      }, 4060),
    ];
    return () => timers.forEach(clearTimeout);
  }, [cycleKey]);

  useEffect(() => {
    if (!showMessage) return;
    const id = setInterval(() => {
      setTipVisible(false);
      setTimeout(() => {
        setTipIdx((i) => (i + 1) % GROCERY_TIPS.length);
        setTipVisible(true);
      }, 380);
    }, 4500);
    return () => clearInterval(id);
  }, [showMessage]);

  const isMoving = phase === "driving-in" || phase === "driving-out";
  const tip = GROCERY_TIPS[tipIdx];

  const scooterLeft =
    phase === "pre"
      ? "-220px"
      : phase === "driving-out" || phase === "done"
      ? "calc(100% + 20px)"
      : "calc(50% - 100px)";
  const scooterTransition =
    phase === "driving-in"
      ? "left 1s cubic-bezier(0.22, 1, 0.36, 1)"
      : phase === "driving-out"
      ? "left 1s cubic-bezier(0.64, 0, 0.78, 0)"
      : "none";

  return (
    <div className="gcd-mascot-root">
      <div className="gcd-mascot-lane">
        {isMoving && (
          <div className="gcd-mascot-speed-wrap">
            <span className="gcd-ms gcd-ms--1" />
            <span className="gcd-ms gcd-ms--2" />
            <span className="gcd-ms gcd-ms--3" />
            <span className="gcd-ms gcd-ms--4" />
            <span className="gcd-ms gcd-ms--5" />
          </div>
        )}
        <div className="gcd-mascot-ground" />
        <div
          className="gcd-mascot-scooter-outer"
          style={{ left: scooterLeft, transition: scooterTransition }}
        >
          <img
            src={PalletScooter}
            alt="Pallet delivery scooter"
            className={`gcd-mascot-scooter-img${bouncing ? " gcd-mascot-scooter-img--bounce" : ""}`}
            draggable={false}
          />
        </div>
      </div>
      {showMessage && (
        <div className="gcd-mascot-tip-row" style={{ opacity: tipVisible ? 1 : 0 }}>
          <span className="gcd-mascot-tip-icon" role="img" aria-hidden="true">
            {tip.icon}
          </span>
          <p className="gcd-mascot-tip-text">{tip.text}</p>
        </div>
      )}
    </div>
  );
};

// ─── GSTIN format: 2-digit state · 5-letter PAN · 4-digit · 1-letter · entity(1-9/A-Z) · Z · checksum
const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const isValidGSTINFormat = (val) => GSTIN_REGEX.test((val || "").trim().toUpperCase());

const GroceryAddCustomerModal = ({
  open,
  cartItems,
  setCartItems,
  setOpenCartCustomerDetail,
  setName,
  setCustomerLoader,
  onCustomerAdded,
  disableClose = false,
}) => {
  const dispatch = useDispatch();
  const showSnackbar = useSnackbar();
  const isMobile = useGetDevice();
  const { renderAuthModal, openAuthModal } = useAuthModal();

  const {
    setCartId,
    setSubTotal,
    setTotal,
    setTotalTaxes,
    setTotalValue,
    setCartTotalQty,
    setCgst,
    setSgst,
    setCess,
    setSaleMode,
  } = useContext(UserContext);

  const locId = localStorage.getItem("locId") || "";
  const orgId = localStorage.getItem("orgId") || "";
  const sessionId = localStorage.getItem("sessionId") || "";
  const licenseId = localStorage.getItem("licenseId") || "";
  const licenseName = localStorage.getItem("licenseName") || "";
  const cartId = localStorage.getItem("CartId") || "";
  const retailUser = JSON.parse(localStorage.getItem("retailUserDetails") || "{}");

  // Show the close button only when the operator is re-opening the modal
  // on an already-active session (cart + customer both exist).
  // When there is no cart yet the operator must complete this flow.
  const _savedCustomer = (() => {
    try {
      return JSON.parse(localStorage.getItem("customer_details") || "{}");
    } catch {
      return {};
    }
  })();
  const showCloseButton = !!cartId && !!(_savedCustomer?.mobileNumber || _savedCustomer?.name);

  // ── Order type + option
  const [selectedOrderType, setSelectedOrderType] = useState("RETAIL");
  const [selectedOption, setSelectedOption] = useState("IN_STORE");

  // ── Retail customer fields
  const [selectedCountry, setSelectedCountry] = useState(
    () =>
      countryOptions.find((c) => c.value === "+91") || {
        label: "India (+91)",
        value: "+91",
        flag: "🇮🇳",
        iso: "IN",
      }
  );
  const [mobile, setMobile] = useState("");
  const [name, setLocalName] = useState("");
  const [addLoading, setAddLoading] = useState(false);
  const [skipLoading, setSkipLoading] = useState(false);
  const [whatsAppCheck, setWhatsAppCheck] = useState(false);
  const [gstLoading, setGstLoading] = useState(false);

  // ── Embedded GST / B2B state ──────────────────────────────────────────────
  const [gstOrderOption, setGstOrderOption] = useState("IN_STORE"); // order fulfilment for GST invoice
  const [recentGstPartners, setRecentGstPartners] = useState([]);
  const [gstWhatsApp, setGstWhatsApp] = useState(false);
  const [gstActiveTab, setGstActiveTab] = useState("organization"); // "organization" | "new"
  const [gstSearchQuery, setGstSearchQuery] = useState("");
  const [gstPartnerList, setGstPartnerList] = useState([]);
  const [gstPartnerListLoading, setGstPartnerListLoading] = useState(false);
  const [gstPartnerListPage, setGstPartnerListPage] = useState(0);
  const [gstPartnerListTotal, setGstPartnerListTotal] = useState(0);
  const [gstSelectedPartner, setGstSelectedPartner] = useState(null);
  const [gstPartnerDetails, setGstPartnerDetails] = useState(null);
  const [gstPartnerDetailsLoading, setGstPartnerDetailsLoading] = useState(false);
  const [gstPartnerDetailsError, setGstPartnerDetailsError] = useState(false);
  const [gstNewGstin, setGstNewGstin] = useState("");
  const [gstValidating, setGstValidating] = useState(false);
  const [gstValidatedData, setGstValidatedData] = useState(null);
  const [gstValidatedBizName, setGstValidatedBizName] = useState("");
  const [gstValidatedLegalName, setGstValidatedLegalName] = useState("");
  const [gstValidatedType, setGstValidatedType] = useState(""); // taxpayer type e.g. "Regular"
  const [gstValidatedStatus, setGstValidatedStatus] = useState(""); // e.g. "Active"
  const [gstValidatedAddr, setGstValidatedAddr] = useState({
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    pincode: "",
  });
  const [gstValidationError, setGstValidationError] = useState("");
  const [gstExistingMatch, setGstExistingMatch] = useState(null); // partner already in system for this GSTIN
  // Pricing mode for B2B order
  const [gstPricingMode, setGstPricingMode] = useState("RETAIL"); // "RETAIL" | "WHOLESALE"

  // Contact fields captured after GST validation / partner selection
  const [gstContactPhone, setGstContactPhone] = useState("");
  const [gstContactPhoneError, setGstContactPhoneError] = useState("");
  const [gstContactName, setGstContactName] = useState("");
  const [gstContactCountry, setGstContactCountry] = useState(
    () =>
      countryOptions.find((c) => c.value === "+91") || {
        label: "India (+91)",
        value: "+91",
        flag: "🇮🇳",
        iso: "IN",
      }
  );

  // ── Customer insights
  const [foundCustomer, setFoundCustomer] = useState(null);
  const [customerLookupLoading, setCustomerLookupLoading] = useState(false);
  const { insights, insightsLoading } = useCustomerInsights({
    uidx: foundCustomer?.uidx,
    id: foundCustomer?.id,
  });
  const tilesLoading = customerLookupLoading || insightsLoading;

  const searchTimer = useRef(null);
  const gstSearchTimerRef = useRef(null);

  // ── Restore or reset when modal opens ────────────────────────────────────
  useEffect(() => {
    if (!open) return;

    const saved = JSON.parse(localStorage.getItem("customer_details") || "{}");
    const activeCartId = localStorage.getItem("CartId");
    const cartType = localStorage.getItem("cartOrderType"); // "SALES_ORDER" or absent
    const defaultCountry = countryOptions.find((c) => c.value === "+91") || {
      label: "India (+91)",
      value: "+91",
      flag: "🇮🇳",
      iso: "IN",
    };

    // ── Always reset GST form fields (re-populated below if B2B cart is active)
    setGstActiveTab("organization");
    setGstSearchQuery("");
    setGstPartnerList([]);
    setGstPartnerListPage(0);
    setGstPartnerListTotal(0);
    setGstSelectedPartner(null);
    setGstPartnerDetails(null);
    setGstPartnerDetailsLoading(false);
    setGstPartnerDetailsError(false);
    setGstNewGstin("");
    setGstValidating(false);
    setGstValidatedData(null);
    setGstValidatedBizName("");
    setGstValidatedLegalName("");
    setGstValidatedType("");
    setGstValidatedStatus("");
    setGstValidatedAddr({ addressLine1: "", addressLine2: "", city: "", state: "", pincode: "" });
    setGstValidationError("");
    setGstExistingMatch(null);
    setGstPricingMode("RETAIL");
    setGstOrderOption("IN_STORE");
    setGstWhatsApp(false);
    setGstContactPhone("");
    setGstContactName("");
    setGstContactPhoneError("");
    setGstContactCountry(defaultCountry);
    setRecentGstPartners(JSON.parse(localStorage.getItem(RECENT_GST_PARTNERS_KEY) || "[]"));

    if (activeCartId && cartType === "SALES_ORDER") {
      // ── Active B2B cart — switch to GST Invoice tab and restore customer info ──
      setSelectedOrderType("GST_INVOICE");
      setSelectedOption("IN_STORE");
      setMobile("");
      setLocalName("");
      setSelectedCountry(defaultCountry);

      // Restore contact details from the saved customer record
      setGstContactPhone(saved?.mobileNumber || "");
      setGstContactName(saved?.name || "");
      setGstPricingMode(localStorage.getItem("cartPricingMode") || "RETAIL");

      // Re-load the partner that was used when the cart was created
      const storedPartnerId = localStorage.getItem("b2bPartnerId");
      const storedPartnerName = localStorage.getItem("b2bPartnerName") || "";
      if (storedPartnerId) {
        // Set a stub immediately so the right panel renders the partner name
        const stub = { retailId: storedPartnerId, displayName: storedPartnerName };
        setGstSelectedPartner(stub);
        setGstPartnerDetailsLoading(true);
        getRetailPartnerDetails(storedPartnerId)
          .then((res) => {
            const detail = res?.data?.data?.retail;
            if (detail) {
              setGstPartnerDetails(detail);
              setGstPartnerDetailsError(false);
            } else {
              setGstPartnerDetailsError(true);
            }
          })
          .catch(() => setGstPartnerDetailsError(true))
          .finally(() => setGstPartnerDetailsLoading(false));
      }
    } else if (activeCartId) {
      // ── Active retail cart — restore retail customer fields ──
      setSelectedOrderType("RETAIL");
      setSelectedOption("IN_STORE");
      setMobile(saved?.mobileNumber || "");
      setLocalName(saved?.name || "");
      setSelectedCountry(defaultCountry);
    } else {
      // ── No active cart — fresh state ──
      setSelectedOrderType("RETAIL");
      setSelectedOption("IN_STORE");
      setMobile("");
      setLocalName("");
      setSelectedCountry(defaultCountry);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Debounced retail customer lookup ──────────────────────────────────────
  useEffect(() => {
    clearTimeout(searchTimer.current);
    const minSearchLength = selectedCountry?.value === "+91" ? 10 : 4;
    if (mobile.length < minSearchLength) {
      setFoundCustomer(null);
      setCustomerLookupLoading(false);
      return;
    }
    setCustomerLookupLoading(true);
    searchTimer.current = setTimeout(() => {
      /*
      customerSearch({ orgId, pageNo: 0, pageSize: 1, mobile: numericMobile, name: nameTerm })
      */
      customerSearchV2Api({ organizationId: orgId, search: mobile, pageNumber: 0, pageSize: 1 })
        .then((res) => {
          const customer = res?.data?.customers?.[0];
          if (customer) {
            setFoundCustomer(customer);
            if (!name) setLocalName(customer.name || customer.firstName || "");
          } else {
            setFoundCustomer(null);
            setLocalName("");
          }
        })
        .catch(() => {
          setLocalName("");
        })
        .finally(() => {
          setCustomerLookupLoading(false);
        });
    }, 500);
  }, [mobile]);

  // ── Load GST partners when order type switches to GST_INVOICE ────────────
  useEffect(() => {
    if (open && selectedOrderType === "GST_INVOICE" && gstActiveTab === "organization") {
      setGstPartnerListPage(0);
      fetchGstPartners("", 0);
    }
  }, [open, selectedOrderType, gstActiveTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Debounced GST partner search ─────────────────────────────────────────
  useEffect(() => {
    clearTimeout(gstSearchTimerRef.current);
    gstSearchTimerRef.current = setTimeout(() => {
      if (selectedOrderType === "GST_INVOICE" && gstActiveTab === "organization") {
        setGstPartnerListPage(0);
        setGstSelectedPartner(null);
        setGstPartnerDetails(null);
        fetchGstPartners(gstSearchQuery, 0);
      }
    }, 500);
    return () => clearTimeout(gstSearchTimerRef.current);
  }, [gstSearchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── GST partner fetch ─────────────────────────────────────────────────────
  const fetchGstPartners = useCallback(
    (query, page, append = false) => {
      setGstPartnerListLoading(true);
      // endDate must be a real date; compute 2 years ahead so all partners are in range
      const endDate = new Date(Date.now() + 2 * 365 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0];
      getRetailPartnerList({
        pageNumber: page,
        pageSize: 20,
        partnerId: orgId,
        partnerType: "RETAIL",
        startDate: "2020-01-01",
        endDate,
        // API field is "searchParameter", not "search"
        ...(query ? { searchParameter: query } : {}),
      })
        .then((res) => {
          const content = res?.data?.data?.retails?.content || [];
          const total = res?.data?.data?.retails?.totalElements || 0;
          if (append) {
            setGstPartnerList((prev) => [...prev, ...content]);
          } else {
            setGstPartnerList(content);
          }
          setGstPartnerListTotal(total);
        })
        .catch((err) => {
          showSnackbar(err?.message || "Failed to load partners.", "error");
        })
        .finally(() => setGstPartnerListLoading(false));
    },
    [orgId] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleSelectGstPartner = (partner) => {
    if (gstSelectedPartner?.retailId === partner.retailId) {
      setGstSelectedPartner(null);
      setGstPartnerDetails(null);
      return;
    }
    setGstSelectedPartner(partner);
    setGstPartnerDetails(null);
    setGstPartnerDetailsError(false);
    setGstPartnerDetailsLoading(true);
    getRetailPartnerDetails(partner.retailId)
      .then((res) => {
        const detail = res?.data?.data?.retail;
        if (!detail) {
          setGstPartnerDetailsError(true);
          showSnackbar("Partner details could not be loaded.", "error");
        } else {
          setGstPartnerDetails(detail);
          setGstPartnerDetailsError(false);
          // Save to recent partners list (max 5)
          setRecentGstPartners((prev) => {
            const next = [partner, ...prev.filter((r) => r.retailId !== partner.retailId)].slice(
              0,
              5
            );
            localStorage.setItem(RECENT_GST_PARTNERS_KEY, JSON.stringify(next));
            return next;
          });
          // Auto-populate contact fields from partner data if not already set
          const partnerPhone =
            detail?.contacts?.[0]?.phoneNo || detail?.phoneNumber || partner?.phoneNumber || "";
          const partnerName =
            detail?.displayName ||
            detail?.customerName ||
            partner?.displayName ||
            partner?.name ||
            "";
          setGstContactPhone((prev) => prev || partnerPhone);
          setGstContactName((prev) => prev || partnerName);
        }
      })
      .catch((err) => {
        setGstPartnerDetailsError(true);
        showSnackbar(err?.message || "Failed to load partner details.", "error");
      })
      .finally(() => setGstPartnerDetailsLoading(false));
  };

  const handleLoadMoreGstPartners = () => {
    const nextPage = gstPartnerListPage + 1;
    setGstPartnerListPage(nextPage);
    fetchGstPartners(gstSearchQuery, nextPage, true);
  };

  const handleRetryGstPartnerDetails = () => {
    if (!gstSelectedPartner) return;
    setGstPartnerDetails(null);
    setGstPartnerDetailsError(false);
    setGstPartnerDetailsLoading(true);
    getRetailPartnerDetails(gstSelectedPartner.retailId)
      .then((res) => {
        const detail = res?.data?.data?.retail;
        if (!detail) {
          setGstPartnerDetailsError(true);
        } else {
          setGstPartnerDetails(detail);
          setGstPartnerDetailsError(false);
        }
      })
      .catch(() => setGstPartnerDetailsError(true))
      .finally(() => setGstPartnerDetailsLoading(false));
  };

  const handleValidateGstin = async () => {
    const gstin = gstNewGstin.trim().toUpperCase();
    if (!isValidGSTINFormat(gstin)) return;
    setGstValidating(true);
    setGstValidatedData(null);
    setGstExistingMatch(null);
    setGstValidatedBizName("");
    setGstValidatedLegalName("");
    setGstValidatedType("");
    setGstValidatedStatus("");
    setGstValidatedAddr({ addressLine1: "", addressLine2: "", city: "", state: "", pincode: "" });
    setGstValidationError("");
    setGstContactPhone("");
    setGstContactName("");
    try {
      // ── Step 1: Check if this GSTIN already exists in our partner list ──
      // GSTIN is globally unique — any result from a GSTIN search is the right customer
      const endDate = new Date(Date.now() + 2 * 365 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0];
      const partnerRes = await getRetailPartnerList({
        pageNumber: 0,
        pageSize: 5,
        partnerId: orgId,
        partnerType: "RETAIL",
        startDate: "2020-01-01",
        endDate,
        searchParameter: gstin,
      });
      const partnerContent = partnerRes?.data?.data?.retails?.content || [];
      // Prefer exact GSTIN field match; fall back to any single result (GSTIN is unique)
      const existingMatch =
        partnerContent.find((p) => (p.gstNumber || p.gstin || "").trim().toUpperCase() === gstin) ||
        (partnerContent.length === 1 ? partnerContent[0] : null);
      if (existingMatch) {
        setGstExistingMatch(existingMatch);
        return; // skip external validation — customer already registered
      }

      // ── Step 2: Not in our system — validate via external GST service ──
      const res = await VerifyGST(gstin);
      // Try multiple response shapes returned by the vendor KYC service
      const d = res?.data?.data?.data || res?.data?.data || res?.data;
      if (d && typeof d === "object" && !Array.isArray(d)) {
        const tradeName = d.tradeName || d.tradeNam || "";
        const legalName = d.legalName || d.lgnm || "";
        const taxpayerType = d.taxpayerType || d.dty || d.ctb || "";
        const gstStatus = d.sts || d.status || d.gstnStatus || "";

        const addr = d.address || d.principalAddress || d.pradr?.addr || {};
        const bno = addr.bno || addr.door || "";
        const bnm = addr.bnm || "";
        const st = addr.st || "";
        const loc = addr.loc || "";
        const dst = addr.dst || "";

        const addressLine1 = [bno, bnm, st].filter(Boolean).join(", ") || addr.addressLine1 || "";
        const addressLine2 = [loc, dst].filter(Boolean).join(", ");

        const resolvedBizName =
          tradeName || legalName || d.businessName || d.displayName || d.name || "";

        /* Require at least a business name OR a GST status to consider the
           validation meaningful. An empty-object 200 response (e.g. for a
           randomly typed but format-valid GSTIN) must not be shown as success. */
        const hasUsefulData = !!(resolvedBizName || gstStatus || taxpayerType);
        if (!hasUsefulData) {
          setGstValidationError(
            "GST details not retrieved. The GSTIN may not be registered or the lookup service is unavailable."
          );
          setGstValidatedData(null);
        } else {
          setGstValidatedBizName(resolvedBizName);
          setGstValidatedLegalName(legalName);
          setGstValidatedType(taxpayerType);
          setGstValidatedStatus(gstStatus);
          setGstValidatedAddr({
            addressLine1,
            addressLine2,
            city: addr.city || addr.dst || addr.loc || "",
            state: addr.state || addr.stcd || "",
            pincode: addr.pinCode || addr.pincode || addr.pncd || "",
          });
          setGstValidatedData({ fetched: true });
        }
      } else {
        setGstValidationError(
          "GST details not retrieved. The GSTIN may not be registered or the lookup service is unavailable."
        );
        setGstValidatedData(null);
      }
    } catch {
      setGstValidationError("Invalid GSTIN. Please enter a valid GST number.");
      setGstValidatedData(null);
    } finally {
      setGstValidating(false);
    }
  };

  // ── Cart payload skeleton ─────────────────────────────────────────────────
  const retailPayload = {
    userName: null,
    userId: null,
    mobileNo: null,
    createdBy: retailUser?.firstName,
    locationId: locId,
    sourceId: orgId,
    loggedInUser: retailUser?.uidx,
    sourceLocationId: locId,
    sourceType: "RETAIL",
    sourceApp: "PALLET_POS",
    destinationId: orgId,
    destinationLocationId: locId,
    destinationType: "RETAIL",
    comments: null,
    sessionId: sessionId || null,
    orderType: "POS_ORDER",
    subOrderType: "POS_ORDER",
    licenseId: licenseId || null,
    hoId: null,
    channel: "POS",
    cartId: cartId || null,
    terminalName: licenseName || null,
  };

  const applyCartResponse = (data) => {
    if (!data) return;
    // Coerce cartId to a plain string and reject bad values before storing
    const rawId = data.cartId;
    const cartIdStr = typeof rawId === "string" || typeof rawId === "number" ? String(rawId) : null;
    const safeCartId =
      cartIdStr && cartIdStr !== "[object Object]" && cartIdStr !== "undefined" ? cartIdStr : null;
    if (safeCartId) {
      setCartId(safeCartId);
      localStorage.setItem("CartId", safeCartId);
    }
    setCartItems(data.cartProducts || []);
    setCartTotalQty(data.billing?.quantity);
    setCgst(data.billing?.cgst);
    setSgst(data.billing?.sgst);
    setCess(data.billing?.totalCessAmount);
    setSubTotal(data.billing?.totalCartValue);
    setTotalTaxes(data.billing?.tax || 0);
    setTotal(data.billing?.totalCartValue);
    setTotalValue(data.billing?.totalValue);
    setSaleMode(data.subOrderType);
    localStorage.setItem("sessionId", sessionId);
  };

  const handleSkip = () => {
    setSkipLoading(true);
    setCustomerLoader(true);
    SkipUserFun({ ...retailPayload })
      .then((res) => {
        if (
          res?.data?.status === "error" ||
          res?.data?.status === "ERROR" ||
          res?.data?.data?.es > 0
        ) {
          const errMsg = res?.data?.message || res?.data?.data?.message || "";
          if (isStaleCartError(errMsg)) {
            clearStaleCartStorage();
            setCartId(null);
            setCartItems([]);
            showStaleCartDialog();
            return;
          }
          showSnackbar(errMsg || "Something went wrong", "error");
          return;
        }
        applyCartResponse(res?.data?.data);
        const noCustomer = { name: null, mobileNumber: null, uidx: null, whatsapp: false };
        localStorage.setItem("customer_details", JSON.stringify(noCustomer));
        // Switching to a retail cart — clear any stale B2B/Sales-Order markers so
        // Cart.js and indexV2.js sync effects flip isSalesOrder back to false.
        localStorage.removeItem("cartOrderType");
        localStorage.removeItem("cartPricingMode");
        localStorage.removeItem("b2bPartnerName");
        localStorage.removeItem("b2bPartnerId");
        if (!cartItems?.length) {
          const t = new Date().getTime();
          dispatch(eventTrackerTime({ startTime: t, startTimeAdded: true }));
          dispatch(changePrevAddedTime(t));
        }
        showSnackbar("Cart created", "success");
      })
      .catch((err) => {
        const errMsg = err?.response?.data?.message || err?.message || "";
        if (isStaleCartError(errMsg)) {
          clearStaleCartStorage();
          setCartId(null);
          setCartItems([]);
          showStaleCartDialog();
          return;
        }
        showSnackbar(errMsg || "Error", "error");
      })
      .finally(() => {
        setSkipLoading(false);
        setCustomerLoader(false);
        setName("");
        setOpenCartCustomerDetail(false);
      });
  };

  const handleAdd = () => {
    const phone = mobile.trim();
    const customerName = name.trim();
    if (!phone) {
      showSnackbar("Please enter mobile number", "error");
      return;
    }
    if (selectedCountry?.value === "+91" && phone.length !== 10) {
      showSnackbar("Enter a valid 10-digit mobile number", "error");
      return;
    }

    setAddLoading(true);
    setCustomerLoader(true);

    const resolvedName = customerName || foundCustomer?.firstName || foundCustomer?.name || "";

    const addPayload = {
      mobile: phone,
      firstName: resolvedName,
      countryCode: (selectedCountry?.value || "+91").replace("+", ""),
      tenantId: locId,
    };

    addCustomer(addPayload)
      .then((response) => {
        const uidx = response?.data?.data?.uidx;
        const linkPayload = {
          uidx,
          phoneNumber: phone,
          name: response?.data?.data?.firstName || resolvedName,
          organizationId: orgId,
          createdBy: retailUser?.uidx,
          locationId: locId,
          sessionId,
          platform: "POS"
        };
        /*
        return AddedCustomerData(linkPayload).then(() => {
        */
        return createB2CcustomerApi(linkPayload).then(() => {
          const payload = {
            ...retailPayload,
            userName: response?.data?.data?.firstName || resolvedName,
            mobileNo: phone,
            userId: uidx,
          };
          return SkipUserFun(payload).then((res) => {
            if (
              res?.data?.status === "error" ||
              res?.data?.status === "ERROR" ||
              res?.data?.data?.es > 0
            ) {
              const errMsg = res?.data?.message || res?.data?.data?.message || "";
              if (isStaleCartError(errMsg)) {
                clearStaleCartStorage();
                setCartId(null);
                setCartItems([]);
                showStaleCartDialog();
                return;
              }
              showSnackbar(errMsg || "Something went wrong", "error");
              return;
            }
            applyCartResponse(res?.data?.data);
            const customerDetails = {
              name: response?.data?.data?.firstName || resolvedName,
              mobileNumber: phone,
              uidx,
              email: response?.data?.data?.email,
              countryCode: response?.data?.data?.countryCode,
              whatsapp: false,
            };
            localStorage.setItem("customer_details", JSON.stringify(customerDetails));
            localStorage.setItem("customerId", uidx);
            // Retail customer added — clear any stale B2B/Sales-Order markers.
            localStorage.removeItem("cartOrderType");
            localStorage.removeItem("cartPricingMode");
            localStorage.removeItem("b2bPartnerName");
            localStorage.removeItem("b2bPartnerId");
            if (!cartItems?.length) {
              const t = new Date().getTime();
              dispatch(eventTrackerTime({ startTime: t, startTimeAdded: true }));
              dispatch(changePrevAddedTime(t));
            }
            setName(response?.data?.data?.firstName || resolvedName);
            showSnackbar("Customer added", "success");
            onCustomerAdded?.();
          });
        });
      })
      .catch((err) => {
        const errMsg = err?.response?.data?.message || err?.message || "";
        if (isStaleCartError(errMsg)) {
          clearStaleCartStorage();
          setCartId(null);
          setCartItems([]);
          showStaleCartDialog();
          return;
        }
        showSnackbar(errMsg || "Error adding customer", "error");
      })
      .finally(() => {
        setAddLoading(false);
        setCustomerLoader(false);
        setOpenCartCustomerDetail(false);
      });
  };

  /* ── GST / SALES_ORDER flow ─────────────────────────────────────────── */

  const _buildAddr = (addr) => ({
    addressId: addr?.id || addr?.addressId || 0,
    country: addr?.country || "India",
    state: addr?.state || "",
    city: addr?.city || "",
    pinCode: addr?.pincode || addr?.pinCode || addr?.pincode || "",
    phoneNo: addr?.mobileNumber || addr?.phoneNo || mobile || "",
    addressLine1: addr?.addressLine1 || "",
    addressLine2: addr?.addressLine2 || "",
    type: "RETAIL",
    updatedBy: retailUser?.uidx || "",
    updatedOn: "",
  });

  const _doCreateSalesOrderCart = (gstContext) => {
    setGstLoading(true);
    const partner = gstContext.partnerDetails || gstContext.partner || {};

    const billingAddr =
      gstContext.partnerDetails?.addresses?.find((a) => a.defaultBilling) ||
      gstContext.partnerDetails?.addresses?.[0] ||
      {};
    const shippingAddr =
      gstContext.partnerDetails?.addresses?.find((a) => a.defaultShipping) || billingAddr;

    const hasAddress =
      (gstContext.type === "ORGANIZATION" || gstContext.type === "NEW_GSTIN") &&
      gstContext.partnerDetails?.addresses?.length > 0;

    const payload = {
      userName:
        gstContext.contactName ||
        partner?.displayName ||
        gstContext.partner?.displayName ||
        "B2B Customer",
      userId: gstContext.partner?.retailId || "",
      mobileNo:
        gstContext.contactPhone ||
        partner?.contacts?.[0]?.phoneNo ||
        gstContext.partner?.phoneNumber ||
        "",
      enableWhatsapp: gstContext.enableWhatsapp || false,
      createdBy: retailUser?.firstName || "",
      locationId: locId,
      orderType: "SALES_ORDER",
      pricingMode: gstPricingMode,
      sourceId: orgId,
      loggedInUser: retailUser?.uidx || "",
      sourceLocationId: locId,
      sourceType: "RETAIL",
      sourceApp: "PALLET_POS",
      destinationId: orgId,
      destinationLocationId: locId,
      destinationType: "RETAIL",
      subOrderType: "B2B_POS",
      licenseId: licenseId || null,
      terminalName: licenseName || null,
      comments: gstContext.gstNumber ? `GST: ${gstContext.gstNumber}` : "",
      ...(hasAddress
        ? {
            address: {
              billingAddress: { ..._buildAddr(billingAddr), gstIn: gstContext.gstNumber || "" },
              shippingAddress: { ..._buildAddr(shippingAddr), gstIn: gstContext.gstNumber || "" },
            },
          }
        : {}),
    };

    createSalesOrderCart(payload)
      .then((res) => {
        if (res?.data?.status === "error" || res?.data?.data?.es > 0) {
          showSnackbar(res?.data?.message || "Failed to create B2B order", "error");
          return;
        }
        const data = res?.data?.data?.data;
        applyCartResponse(data);
        localStorage.setItem("cartOrderType", "SALES_ORDER");
        localStorage.setItem("cartPricingMode", gstPricingMode);
        localStorage.setItem(
          "cartSaleMode",
          gstOrderOption === "DELIVERY" ? "delivery" : "instore"
        );
        const partnerName =
          partner?.displayName || gstContext.partner?.displayName || "B2B Customer";
        localStorage.setItem("b2bPartnerName", partnerName);
        // Store retailId so modal can restore partner selection when re-opened
        const partnerId = partner?.retailId || gstContext.partner?.retailId || "";
        if (partnerId) localStorage.setItem("b2bPartnerId", partnerId);
        else localStorage.removeItem("b2bPartnerId");
        localStorage.setItem(
          "customer_details",
          JSON.stringify({
            name: gstContext.contactName || partnerName,
            mobileNumber:
              gstContext.contactPhone ||
              partner?.contacts?.[0]?.phoneNo ||
              gstContext.partner?.phoneNumber ||
              "",
            uidx: null,
            whatsapp: gstContext.enableWhatsapp || false,
          })
        );
        setName(gstContext.contactName || partnerName);
        setOpenCartCustomerDetail(false);
        showSnackbar("B2B Sales Order created", "success");
      })
      .catch((err) =>
        showSnackbar(err?.message || "Failed to create B2B order. Please try again.", "error")
      )
      .finally(() => setGstLoading(false));
  };

  const handleGstConfirm = (gstContext) => {
    const itemCount = cartItems?.length || 0;
    if (itemCount > 0) {
      Swal.fire({
        title: "Start a fresh B2B order?",
        html: `Your current cart has <strong>${itemCount} item${
          itemCount === 1 ? "" : "s"
        }</strong>. Creating a B2B/GST Sales Order will start a <strong>new empty cart</strong>. The existing items will be discarded.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, create B2B order",
        cancelButtonText: "Keep current cart",
        customClass: {
          confirmButton: "button button-success",
          cancelButton: "button button-error",
          container: "close-session-swal-container",
        },
        buttonsStyling: false,
        backdrop: "rgba(0,0,0,0.4)",
      }).then((result) => {
        if (result.isConfirmed) {
          _doCreateSalesOrderCart(gstContext);
        } else {
          // Reset any loading started before the confirmation dialog
          setGstLoading(false);
          setSelectedOrderType("RETAIL");
        }
      });
    } else {
      _doCreateSalesOrderCart(gstContext);
    }
  };

  /* ── Called from footer "Create B2B Order" button ── */
  const handleCreateB2BOrder = () => {
    if (gstActiveTab === "organization") {
      if (!gstSelectedPartner || !gstPartnerDetails) return;
      handleGstConfirm({
        type: "ORGANIZATION",
        partner: gstSelectedPartner,
        partnerDetails: gstPartnerDetails,
        gstNumber: gstPartnerDetails?.gstNumber || null,
        contactPhone: gstContactPhone.trim() || null,
        contactName: gstContactName.trim() || null,
        enableWhatsapp: gstWhatsApp,
      });
    } else {
      // ── New customer tab ── validate, create B2B customer, then create cart ──
      const gstin = gstNewGstin.trim().toUpperCase();
      if (!isValidGSTINFormat(gstin) || !gstValidatedData) return;

      // Mobile is mandatory for B2B customer creation
      const phone = gstContactPhone.trim();
      if (!gstContactPhoneValid) {
        setGstContactPhoneError(
          phone
            ? "Enter a valid 10-digit mobile number"
            : "Mobile number is required to create a B2B customer"
        );
        return;
      }
      setGstContactPhoneError("");

      const bizName = gstValidatedBizName || gstContactName.trim() || "B2B Customer";
      const now = new Date().toISOString();

      // Build the full phone string including dial code (e.g. "+919876543210")
      const dialCode = gstContactCountry?.value || "+91";
      const fullPhone = dialCode + phone;

      const customerPayload = {
        logoUrl: "",
        website: "",
        retailType: "WHOLESALERS",
        source: "RETAIL",
        displayName: bizName,
        panNumber: "",
        gstNumber: gstin,
        businessType: "Wholesalers",
        name: bizName,
        createdBy: retailUser?.uidx || "",
        updatedBy: retailUser?.uidx || "",
        customerName: bizName,
        gstTreatment: gstValidatedType
          ? `Registered Business - ${gstValidatedType}`
          : "Registered Business - Regular",
        supplyTo: gstValidatedAddr.state || "",
        taxPreference: "Taxable",
        currency: "INR",
        paymentTerms: "Due end of Month",
        priceSlab: "Price Slab 1",
        preAssignedCouponCode: "",
        accessPortal: "",
        partnerDetails: [{ partnerId: orgId, partnerType: "RETAIL", partner: {} }],
        contacts: [
          {
            contactName: gstContactName.trim() || bizName,
            phoneNo: fullPhone,
            email: "",
            contactType: "SUPPORT",
            priority: "string",
            entityId: "string",
            entityType: "RETAIL",
            createdBy: retailUser?.uidx || "",
          },
        ],
        addresses:
          gstValidatedAddr.addressLine1 || gstValidatedAddr.city
            ? [
                {
                  entityId: null,
                  entityType: "RETAIL",
                  addressLine1: gstValidatedAddr.addressLine1 || "",
                  addressLine2: gstValidatedAddr.addressLine2 || "",
                  country: "India",
                  state: gstValidatedAddr.state || "",
                  city: gstValidatedAddr.city || "",
                  pincode: gstValidatedAddr.pincode || "",
                  createdBy: retailUser?.uidx || "",
                  defaultShipping: "true",
                  defaultBilling: "true",
                  defaultAddress: "true",
                  name: bizName,
                  mobileNumber: fullPhone,
                },
              ]
            : [],
        kycRequest: {
          entityType: "RETAIL",
          entityId: null,
          documents: [
            {
              documentType: "BANK_DETAILS",
              documentUrl: null,
              bankAccountNumber: "",
              ifscCode: "",
              accountHolderName: "",
              gstNumber: gstin,
            },
          ],
          createdBy: retailUser?.uidx || "",
        },
        createdAt: now,
        modifiedBy: null,
        modifiedAt: now,
        warehouseId: orgId,
        kycVerified: true,
      };

      setGstLoading(true);
      createB2BCustomer(customerPayload)
        .then((res) => {
          const retail = res?.data?.data?.retail;
          if (!retail?.retailId) {
            showSnackbar(res?.data?.message || "Failed to create B2B customer", "error");
            setGstLoading(false);
            return;
          }
          // Customer created — now create the SALES_ORDER cart using the new retailId
          handleGstConfirm({
            type: "NEW_GSTIN",
            partner: { retailId: retail.retailId, displayName: retail.displayName || bizName },
            partnerDetails: {
              retailId: retail.retailId,
              displayName: retail.displayName || bizName,
              contacts: retail.contacts || [],
              addresses: retail.addresses || [],
              gstNumber: gstin,
            },
            gstNumber: gstin,
            contactPhone: phone,
            contactName: gstContactName.trim() || null,
            enableWhatsapp: gstWhatsApp,
          });
        })
        .catch((err) => {
          showSnackbar(err?.message || "Failed to create B2B customer. Please try again.", "error");
          setGstLoading(false);
        });
    }
  };

  const handlePhoneChange = (val) => {
    setMobile(val);
    setFoundCustomer(null);
  };

  const handleCountryChange = (selectedOption) => {
    setSelectedCountry(selectedOption);
  };

  const handleKeyPress = (e) => {
    if (e.key !== "Enter") return;
    if (addLoading || skipLoading) return;
    if (selectedOrderType === "GST_INVOICE") return; // Enter doesn't submit in GST mode
    if (!mobile.trim()) {
      handleSkip();
      return;
    }
    if (canAdd) {
      handleAdd();
    }
  };

  const newSwal = Swal.mixin({
    customClass: {
      confirmButton: "button button-success",
      cancelButton: "button button-error",
      container: "close-session-swal-container",
    },
    buttonsStyling: false,
  });

  const clearLC = () => {
    newSwal
      .fire({
        title: "Are you sure you want to clear the local storage?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Proceed",
        cancelButtonText: "Cancel",
        backdrop: "rgba(0,0,0,0.4)",
      })
      .then((result) => {
        if (result.isConfirmed) {
          openAuthModal(() => {
            localStorage.clear();
            newSwal.fire({
              title: "Cleared!",
              text: "Local storage has been cleared.",
              icon: "success",
              timer: 1500,
              showConfirmButton: false,
            });
          });
        }
      });
  };

  const isValidMobile =
    selectedCountry?.value === "+91" ? (mobile || "").length === 10 : (mobile || "").length >= 4;

  const canAdd = isValidMobile && selectedOrderType === "RETAIL";

  // Mobile validity helper (shared between org + new tab checks)
  const gstContactPhoneValid =
    gstContactCountry?.value === "+91"
      ? gstContactPhone.trim().length === 10
      : gstContactPhone.trim().length >= 4;

  // Can create B2B order when:
  //   • org tab: partner selected, details loaded, no error
  //   • new tab: valid GSTIN format + GST data validated + valid mobile (mandatory for customer creation)
  const canCreateB2BOrder =
    selectedOrderType === "GST_INVOICE" &&
    !gstLoading &&
    ((gstActiveTab === "organization" &&
      gstSelectedPartner != null &&
      !gstPartnerDetailsLoading &&
      gstPartnerDetails != null &&
      !gstPartnerDetailsError) ||
      (gstActiveTab === "new" &&
        isValidGSTINFormat(gstNewGstin) &&
        gstValidatedData != null &&
        gstContactPhoneValid));

  const saleModeLabel =
    selectedOption === "IN_STORE"
      ? "In-store"
      : selectedOption === "CURBSIDE"
      ? "Curbside pick up"
      : selectedOption === "DELIVERY"
      ? "Delivery"
      : "In-store";

  if (!open) return null;

  return (
    <Modal
      open={open}
      disableEscapeKeyDown
      onClose={(event, reason) => {
        if (disableClose) return;
        if (reason !== "backdropClick") {
          setOpenCartCustomerDetail(false);
        }
      }}
    >
      <Box
        sx={{
          position: "fixed",
          top: 0,
          right: 0,
          height: "100%",
          width: isMobile ? "100%" : "min(980px, 100vw)",
          bgcolor: "#fff",
          borderRadius: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 40px 120px rgba(15,23,42,0.28)",
          borderLeft: "1px solid rgba(255, 255, 255, 0.7)",
          animation: "ccdSlideIn 0.25s ease",
          transition: "width 0.25s ease",
          zIndex: 1400,
        }}
      >
        {renderAuthModal()}

        {/* ── HEADER ── */}
        <div className="ccd-header-ref">
          <h2 className="ccd-title-ref" data-testid="text-customer-popup-title">
            Add customer to this order
          </h2>
          {showCloseButton && (
            <button
              className="ccd-close-btn-ref"
              onClick={() => setOpenCartCustomerDetail(false)}
              data-testid="button-customer-modal-close"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ width: "16px", height: "16px", color: "#64748b" }}
                aria-hidden="true"
              >
                <path d="M18 6 6 18"></path>
                <path d="m6 6 12 12"></path>
              </svg>
            </button>
          )}
        </div>

        {/* ── Pallet delivery mascot — only shown when retail + no valid mobile ── */}
        {selectedOrderType === "RETAIL" && !isValidMobile && <GcdDeliveryMascot />}

        {/* ── SPLIT GRID LAYOUT ── */}
        <div className="ccd-split-grid">
          {/* ── FORM SIDE (Left Column) ── */}
          <div className="ccd-form-side-ref">
            <div className="ccd-form-content-ref">
              <div className="ccd-form-scrollable-ref">
                {/* ── ORDER TYPE selector ── */}
                <div className="ccd-order-type-section-ref">
                  <p className="ccd-section-label-ref">Order type</p>
                  <div className="ccd-order-type-row-ref">
                    {ORDER_TYPES.map(({ id, label }) => (
                      <button
                        key={id}
                        type="button"
                        className={`ccd-order-type-btn-ref${
                          selectedOrderType === id ? " active" : ""
                        }`}
                        onClick={() => {
                          setSelectedOrderType(id);
                          if (id === "RETAIL") {
                            // Reset GST state when switching back
                            setGstSelectedPartner(null);
                            setGstPartnerDetails(null);
                            setGstNewGstin("");
                            setGstValidatedData(null);
                            setGstValidatedBizName("");
                            setGstValidatedAddr({
                              addressLine1: "",
                              city: "",
                              state: "",
                              pincode: "",
                            });
                          }
                        }}
                        data-testid={`button-order-type-${id.toLowerCase()}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* ── ORDER OPTIONS (only for RETAIL) ── */}
                {selectedOrderType === "RETAIL" && (
                  <div className="ccd-order-option-section-ref">
                    <p className="ccd-section-label-ref">Order option</p>
                    <div className="ccd-option-grid-ref">
                      {ORDER_OPTIONS.map(({ id, label, Icon, disabled }) => {
                        const isActive = selectedOption === id;
                        const testId =
                          id === "IN_STORE"
                            ? "button-customer-order-option-in-store"
                            : id === "CURBSIDE"
                            ? "button-customer-order-option-curbside"
                            : "button-customer-order-option-delivery";
                        return (
                          <button
                            key={id}
                            type="button"
                            disabled={disabled}
                            data-testid={testId}
                            className={`ccd-option-card-ref${isActive ? " active" : ""}${
                              disabled ? " disabled" : ""
                            }`}
                            onClick={() => {
                              if (!disabled) setSelectedOption(id);
                            }}
                          >
                            <div className="ccd-option-card-inner-ref">
                              <div className="ccd-option-icon-container-ref">
                                <Icon />
                              </div>
                              <p className="ccd-option-text-ref">{label}</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ── PHONE + NAME (retail only — GST mode captures contact after validation) ── */}
                {selectedOrderType === "RETAIL" && (
                  <div className="ccd-inputs-grid-ref">
                    <div>
                      <label className="ccd-input-label-ref">Mobile number</label>
                      <div className="ccd-mobile-input-row-ref">
                        <FormControl variant="standard" sx={{ width: "100%" }}>
                          <NewCountryCodeInput
                            countryOptions={countryOptions}
                            customerMobileNumber={mobile}
                            setCustomerMobileNumber={handlePhoneChange}
                            handleKeyPress={handleKeyPress}
                            selectedCountry={selectedCountry}
                            handleCountryChange={handleCountryChange}
                          />
                        </FormControl>
                      </div>
                    </div>
                    {selectedOrderType === "RETAIL" && (
                      <div>
                        <label className="ccd-input-label-ref">Customer name</label>
                        <FormControl variant="standard" sx={{ width: "100%" }}>
                          <div
                            style={{
                              position: "relative",
                              width: "100%",
                              display: "flex",
                              alignItems: "center",
                              height: "48px",
                            }}
                          >
                            <BootstrapInput
                              sx={{
                                width: "100%",
                                "& .MuiInputBase-input": {
                                  backgroundColor: "#ffffff",
                                  border: "1px solid #e2e8f0",
                                  borderRadius: "20px",
                                  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05) !important",
                                  fontSize: "0.875rem !important",
                                  fontWeight: "500 !important",
                                  color: "#333333 !important",
                                  padding: insightsLoading
                                    ? "12px 40px 12px 16px !important"
                                    : "12px 16px !important",
                                  height: "48px !important",
                                  boxSizing: "border-box !important",
                                  "&::placeholder": {
                                    color: "#65758B !important",
                                    opacity: 1,
                                    fontSize: "0.875rem !important",
                                    fontWeight: "400 !important",
                                  },
                                  "&.Mui-disabled": {
                                    backgroundColor: "#f1f5f9 !important",
                                    color: "#94a3b8 !important",
                                    WebkitTextFillColor: "#94a3b8 !important",
                                    cursor: "not-allowed !important",
                                  },
                                },
                              }}
                              className="customer-name-new"
                              placeholder="Enter customer name"
                              id="bootstrap-input"
                              value={name}
                              onChange={(e) => setLocalName(e.target.value)}
                              onKeyDown={handleKeyPress}
                              disabled={!mobile}
                              data-testid="input-customer-name"
                            />
                            {insightsLoading && (
                              <CircularProgress
                                size={18}
                                sx={{
                                  position: "absolute",
                                  right: "16px",
                                  top: "30%",
                                  transform: "translateY(-50%)",
                                  color: "#2a71ff",
                                  zIndex: 10,
                                }}
                              />
                            )}
                          </div>
                        </FormControl>
                      </div>
                    )}
                  </div>
                )}

                {/* ── EMBEDDED GST SECTION (GST_INVOICE type only) ── */}
                {selectedOrderType === "GST_INVOICE" && (
                  <div className="ccd-gst-inline-ref">
                    {/* Order option selector */}
                    <p className="ccd-section-label-ref" style={{ marginBottom: "8px" }}>
                      Order type
                    </p>
                    <div className="ccd-gst-order-options-ref">
                      {GST_ORDER_OPTIONS.map(({ id, label, Icon }) => (
                        <button
                          key={id}
                          type="button"
                          className={`ccd-gst-order-opt-ref${
                            gstOrderOption === id ? " active" : ""
                          }`}
                          onClick={() => setGstOrderOption(id)}
                          data-testid={`gst-order-opt-${id.toLowerCase()}`}
                        >
                          <Icon size={18} />
                          <span>{label}</span>
                        </button>
                      ))}
                    </div>

                    <p
                      className="ccd-section-label-ref"
                      style={{ marginTop: "14px", marginBottom: "10px" }}
                    >
                      Organisation
                    </p>

                    {/* Tabs */}
                    <div className="ccd-gst-tabs-ref">
                      <button
                        className={`ccd-gst-tab-ref${
                          gstActiveTab === "organization" ? " active" : ""
                        }`}
                        onClick={() => {
                          setGstActiveTab("organization");
                          if (gstPartnerList.length === 0) fetchGstPartners("", 0);
                        }}
                        data-testid="gst-tab-organization"
                      >
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M3 9h18v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9Z" />
                          <path d="m3 9 2.45-4.9A2 2 0 0 1 7.24 3h9.52a2 2 0 0 1 1.8 1.1L21 9" />
                          <path d="M12 3v6" />
                        </svg>
                        Existing organisation
                      </button>
                      <button
                        className={`ccd-gst-tab-ref${gstActiveTab === "new" ? " active" : ""}`}
                        onClick={() => setGstActiveTab("new")}
                        data-testid="gst-tab-new"
                      >
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <path d="M12 8v8M8 12h8" />
                        </svg>
                        New customer
                      </button>
                    </div>

                    {gstActiveTab === "organization" ? (
                      <div className="ccd-gst-org-tab-ref">
                        {/* Search */}
                        <div className="ccd-gst-search-wrap-ref">
                          <svg
                            className="ccd-gst-search-icon-ref"
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <circle cx="11" cy="11" r="8" />
                            <path d="m21 21-4.3-4.3" />
                          </svg>
                          <input
                            className="ccd-gst-search-input-ref"
                            placeholder="Search by name, phone or GST number…"
                            value={gstSearchQuery}
                            onChange={(e) => setGstSearchQuery(e.target.value)}
                            data-testid="gst-org-search"
                          />
                          {gstPartnerListLoading && gstPartnerList.length === 0 && (
                            <CircularProgress size={14} sx={{ color: "#2a71ff", flexShrink: 0 }} />
                          )}
                        </div>

                        {/* Partner list */}
                        <div className="ccd-gst-partner-list-ref">
                          {/* Recently used — shown when search is empty */}
                          {gstSearchQuery.trim() === "" && recentGstPartners.length > 0 && (
                            <div className="ccd-gst-recent-section-ref">
                              <p className="ccd-gst-list-group-label-ref">Recently used</p>
                              {recentGstPartners.map((p) => {
                                const isSel = gstSelectedPartner?.retailId === p.retailId;
                                return (
                                  <button
                                    key={`recent-${p.retailId}`}
                                    className={`ccd-gst-partner-card-ref recent${
                                      isSel ? " selected" : ""
                                    }`}
                                    onClick={() => handleSelectGstPartner(p)}
                                    data-testid={`gst-recent-partner-${p.retailId}`}
                                  >
                                    <div
                                      className="ccd-gst-partner-avatar-ref"
                                      data-initial={(p.displayName ||
                                        p.name ||
                                        "?")[0].toUpperCase()}
                                    >
                                      {(p.displayName || p.name || "?")[0].toUpperCase()}
                                    </div>
                                    <div className="ccd-gst-partner-info-ref">
                                      <p className="ccd-gst-partner-name-ref">
                                        {p.displayName || p.name || "—"}
                                      </p>
                                      <p className="ccd-gst-partner-sub-ref">
                                        {p.phoneNumber || p.email || p.retailId}
                                      </p>
                                    </div>
                                    {isSel && (
                                      <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="#2a71ff"
                                        strokeWidth="2.5"
                                      >
                                        <polyline points="20 6 9 17 4 12" />
                                      </svg>
                                    )}
                                  </button>
                                );
                              })}
                              {gstPartnerList.length > 0 && (
                                <p
                                  className="ccd-gst-list-group-label-ref"
                                  style={{ marginTop: "6px" }}
                                >
                                  All partners
                                </p>
                              )}
                            </div>
                          )}

                          {gstPartnerList.length === 0 && !gstPartnerListLoading ? (
                            <div className="ccd-gst-empty-ref">
                              <svg
                                width="28"
                                height="28"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#94a3b8"
                                strokeWidth="1.5"
                              >
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                <circle cx="9" cy="7" r="4" />
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                              </svg>
                              <p className="ccd-gst-empty-title-ref">No partners found</p>
                              <p className="ccd-gst-empty-sub-ref">
                                Try a different search, or register as a new customer.
                              </p>
                            </div>
                          ) : (
                            gstPartnerList.map((p) => {
                              const isSel = gstSelectedPartner?.retailId === p.retailId;
                              return (
                                <button
                                  key={p.retailId}
                                  className={`ccd-gst-partner-card-ref${isSel ? " selected" : ""}`}
                                  onClick={() => handleSelectGstPartner(p)}
                                  data-testid={`gst-partner-${p.retailId}`}
                                >
                                  <div className="ccd-gst-partner-avatar-ref">
                                    {(p.displayName || p.name || "?")[0].toUpperCase()}
                                  </div>
                                  <div className="ccd-gst-partner-info-ref">
                                    <p className="ccd-gst-partner-name-ref">
                                      {p.displayName || p.name || "—"}
                                    </p>
                                    <p className="ccd-gst-partner-sub-ref">
                                      {p.phoneNumber || p.email || p.retailId}
                                    </p>
                                  </div>
                                  {isSel && (
                                    <svg
                                      width="16"
                                      height="16"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="#2a71ff"
                                      strokeWidth="2.5"
                                    >
                                      <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                  )}
                                </button>
                              );
                            })
                          )}

                          {gstPartnerListLoading && gstPartnerList.length > 0 && (
                            <div className="ccd-gst-list-spinner-ref">
                              <CircularProgress size={18} sx={{ color: "#2a71ff" }} />
                            </div>
                          )}

                          {gstPartnerList.length > 0 &&
                            gstPartnerList.length < gstPartnerListTotal &&
                            !gstPartnerListLoading && (
                              <button
                                className="ccd-gst-load-more-ref"
                                onClick={handleLoadMoreGstPartners}
                              >
                                Load more ({gstPartnerListTotal - gstPartnerList.length} remaining)
                              </button>
                            )}
                        </div>

                        {/* Partner detail loading spinner (details now shown in right pane) */}
                        {gstSelectedPartner && gstPartnerDetailsLoading && (
                          <div className="ccd-gst-detail-loading-inline-ref">
                            <CircularProgress size={14} sx={{ color: "#2a71ff" }} />
                            <span>Loading partner details…</span>
                          </div>
                        )}
                        {gstSelectedPartner && gstPartnerDetailsError && (
                          <div className="ccd-gst-detail-error-ref">
                            <p>Could not load details. Confirm is unavailable.</p>
                            <button
                              className="ccd-gst-retry-btn-ref"
                              onClick={handleRetryGstPartnerDetails}
                              disabled={gstPartnerDetailsLoading}
                            >
                              {gstPartnerDetailsLoading ? (
                                <CircularProgress size={12} sx={{ color: "#d97706" }} />
                              ) : (
                                "Retry"
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      /* ── New customer tab ── */
                      <div className="ccd-gst-new-tab-ref">
                        <div className="ccd-gst-info-card-ref">
                          <svg
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z" />
                            <path d="M14 8H8M16 12H8M13 16H8" />
                          </svg>
                          <p>
                            Enter the 15-digit GSTIN to generate a GST invoice and auto-populate the
                            billing address.
                          </p>
                        </div>

                        <div className="ccd-gst-gstin-wrap-ref">
                          <label className="ccd-input-label-ref">GSTIN (15 digits)</label>
                          <div className="ccd-gst-gstin-row-ref">
                            <input
                              className="ccd-gst-gstin-input-ref"
                              placeholder="e.g. 29ABCDE1234F1Z5"
                              maxLength={15}
                              value={gstNewGstin}
                              onChange={(e) => {
                                setGstNewGstin(e.target.value.toUpperCase());
                                setGstValidatedData(null);
                                setGstExistingMatch(null);
                                setGstValidatedBizName("");
                                setGstValidatedAddr({
                                  addressLine1: "",
                                  city: "",
                                  state: "",
                                  pincode: "",
                                });
                                setGstValidationError("");
                                setGstContactPhone("");
                                setGstContactName("");
                              }}
                              data-testid="gst-new-gstin-input"
                            />
                            <button
                              className="ccd-gst-validate-btn-ref"
                              onClick={handleValidateGstin}
                              disabled={!isValidGSTINFormat(gstNewGstin) || gstValidating}
                              data-testid="gst-validate-btn"
                            >
                              {gstValidating ? (
                                <CircularProgress size={14} sx={{ color: "#fff" }} />
                              ) : (
                                "Validate"
                              )}
                            </button>
                          </div>
                          {gstNewGstin.length > 0 && (
                            <p
                              className={`ccd-gst-hint-ref${
                                isValidGSTINFormat(gstNewGstin)
                                  ? " valid"
                                  : gstNewGstin.length === 15
                                  ? " invalid"
                                  : ""
                              }`}
                            >
                              {isValidGSTINFormat(gstNewGstin)
                                ? "✓ Valid GSTIN format"
                                : gstNewGstin.length < 15
                                ? `${gstNewGstin.length} / 15 characters`
                                : "✗ Invalid GSTIN — expected format: 29ABCDE1234F1Z5"}
                            </p>
                          )}
                        </div>

                        {/* Validation error */}
                        {gstValidationError && (
                          <p className="ccd-gst-error-ref" data-testid="gst-validation-error">
                            <svg
                              width="13"
                              height="13"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              style={{ flexShrink: 0 }}
                            >
                              <circle cx="12" cy="12" r="10" />
                              <path d="M12 8v4M12 16h.01" />
                            </svg>
                            {gstValidationError}
                          </p>
                        )}

                        {/* ── Existing customer match ── */}
                        {gstExistingMatch && (
                          <div
                            className="ccd-gst-existing-match-ref"
                            data-testid="gst-existing-match"
                          >
                            <div className="ccd-gst-existing-match-header-ref">
                              <svg
                                width="13"
                                height="13"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                              >
                                <path d="M20 6 9 17l-5-5" />
                              </svg>
                              Already registered in your system
                            </div>
                            <div className="ccd-gst-existing-match-body-ref">
                              <div className="ccd-gst-partner-avatar-ref" style={{ flexShrink: 0 }}>
                                {(gstExistingMatch.displayName ||
                                  gstExistingMatch.name ||
                                  "?")[0].toUpperCase()}
                              </div>
                              <div className="ccd-gst-existing-match-info-ref">
                                <p className="ccd-gst-partner-name-ref">
                                  {gstExistingMatch.displayName || gstExistingMatch.name || "—"}
                                </p>
                                <p className="ccd-gst-partner-sub-ref">
                                  {gstExistingMatch.gstNumber ||
                                    gstExistingMatch.gstin ||
                                    gstNewGstin}
                                </p>
                                {gstExistingMatch.phoneNumber && (
                                  <p className="ccd-gst-partner-sub-ref">
                                    {gstExistingMatch.phoneNumber}
                                  </p>
                                )}
                              </div>
                            </div>
                            <button
                              className="ccd-gst-use-existing-btn-ref"
                              data-testid="gst-use-existing-btn"
                              onClick={() => {
                                handleSelectGstPartner(gstExistingMatch);
                                setGstActiveTab("organization");
                                setGstExistingMatch(null);
                              }}
                            >
                              <svg
                                width="13"
                                height="13"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                              >
                                <path d="M20 6 9 17l-5-5" />
                              </svg>
                              Use this customer
                            </button>
                          </div>
                        )}

                        {/* Auto-populated address fields */}
                        {gstValidatedData && (
                          <div className="ccd-gst-validated-card-ref">
                            <p className="ccd-gst-validated-title-ref">
                              {gstValidatedData.fetched
                                ? "✓ Details fetched — edit if needed"
                                : "Enter details manually"}
                            </p>

                            {/* GST type + status chips */}
                            {(gstValidatedType || gstValidatedStatus) && (
                              <div className="ccd-gst-meta-chips-ref">
                                {gstValidatedType && (
                                  <span className="ccd-gst-type-chip-ref">{gstValidatedType}</span>
                                )}
                                {gstValidatedStatus && (
                                  <span
                                    className={`ccd-gst-status-chip-ref${
                                      gstValidatedStatus.toLowerCase() === "active"
                                        ? " active"
                                        : " inactive"
                                    }`}
                                  >
                                    {gstValidatedStatus}
                                  </span>
                                )}
                              </div>
                            )}

                            {/* Trade name */}
                            <div className="ccd-gst-val-field-ref">
                              <label className="ccd-input-label-ref">Trade / business name</label>
                              <input
                                className="ccd-gst-val-input-ref"
                                value={gstValidatedBizName}
                                onChange={(e) => setGstValidatedBizName(e.target.value)}
                                placeholder="Trade or business name"
                                data-testid="gst-biz-name"
                              />
                            </div>

                            {/* Legal name (read-only, shown if different) */}
                            {gstValidatedLegalName &&
                              gstValidatedLegalName !== gstValidatedBizName && (
                                <div className="ccd-gst-val-field-ref">
                                  <label className="ccd-input-label-ref">
                                    Legal / proprietory name
                                  </label>
                                  <input
                                    className="ccd-gst-val-input-ref"
                                    value={gstValidatedLegalName}
                                    onChange={(e) => setGstValidatedLegalName(e.target.value)}
                                    placeholder="Registered legal name"
                                    data-testid="gst-legal-name"
                                  />
                                </div>
                              )}

                            {/* Address */}
                            <div className="ccd-gst-val-field-ref">
                              <label className="ccd-input-label-ref">Address line 1</label>
                              <input
                                className="ccd-gst-val-input-ref"
                                value={gstValidatedAddr.addressLine1}
                                onChange={(e) =>
                                  setGstValidatedAddr((a) => ({
                                    ...a,
                                    addressLine1: e.target.value,
                                  }))
                                }
                                placeholder="Door no, building, street…"
                                data-testid="gst-addr-line1"
                              />
                            </div>
                            <div className="ccd-gst-val-field-ref">
                              <label className="ccd-input-label-ref">Address line 2</label>
                              <input
                                className="ccd-gst-val-input-ref"
                                value={gstValidatedAddr.addressLine2}
                                onChange={(e) =>
                                  setGstValidatedAddr((a) => ({
                                    ...a,
                                    addressLine2: e.target.value,
                                  }))
                                }
                                placeholder="Locality, area…"
                                data-testid="gst-addr-line2"
                              />
                            </div>
                            <div className="ccd-gst-val-grid-ref">
                              <div className="ccd-gst-val-field-ref">
                                <label className="ccd-input-label-ref">City</label>
                                <input
                                  className="ccd-gst-val-input-ref"
                                  value={gstValidatedAddr.city}
                                  onChange={(e) =>
                                    setGstValidatedAddr((a) => ({ ...a, city: e.target.value }))
                                  }
                                  placeholder="City"
                                  data-testid="gst-addr-city"
                                />
                              </div>
                              <div className="ccd-gst-val-field-ref">
                                <label className="ccd-input-label-ref">State</label>
                                <input
                                  className="ccd-gst-val-input-ref"
                                  value={gstValidatedAddr.state}
                                  onChange={(e) =>
                                    setGstValidatedAddr((a) => ({ ...a, state: e.target.value }))
                                  }
                                  placeholder="State"
                                  data-testid="gst-addr-state"
                                />
                              </div>
                              <div className="ccd-gst-val-field-ref">
                                <label className="ccd-input-label-ref">PIN code</label>
                                <input
                                  className="ccd-gst-val-input-ref"
                                  value={gstValidatedAddr.pincode}
                                  onChange={(e) =>
                                    setGstValidatedAddr((a) => ({ ...a, pincode: e.target.value }))
                                  }
                                  placeholder="PIN"
                                  maxLength={6}
                                  data-testid="gst-addr-pin"
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* ── WhatsApp notification toggle (retail only) ── */}
                {selectedOrderType === "RETAIL" && (
                  <div className="gcd-whatsapp-row">
                    <label className="gcd-whatsapp-label" htmlFor="gcd-whatsapp-chk">
                      <div className="gcd-whatsapp-left">
                        <span className="gcd-whatsapp-icon" aria-hidden="true">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                          </svg>
                        </span>
                        <span className="gcd-whatsapp-text">Get notification on WhatsApp</span>
                      </div>
                      <div
                        className={`gcd-whatsapp-toggle${
                          whatsAppCheck ? " gcd-whatsapp-toggle--on" : ""
                        }`}
                      >
                        <div className="gcd-whatsapp-thumb" />
                      </div>
                      <input
                        type="checkbox"
                        id="gcd-whatsapp-chk"
                        checked={whatsAppCheck}
                        onChange={(e) => setWhatsAppCheck(e.target.checked)}
                        style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
                      />
                    </label>
                    <p className="gcd-whatsapp-note">
                      Sending WhatsApp notifications requires a WhatsApp API integration. Connect
                      your own number, or subscribe to a <strong>Pallet Notifications plan</strong>{" "}
                      to send messages via a Pallet-managed number.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── INSIGHTS SIDE (Right Column) ── */}
          <div className="ccd-insights-side-ref">
            <div className="ccd-insights-content-ref">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <p className="ccd-insights-title-ref">
                  {selectedOrderType === "GST_INVOICE"
                    ? "GST customer details"
                    : "Customer insights"}
                </p>
                <IconButton
                  onClick={clearLC}
                  sx={{ color: "#FF0000", padding: "4px" }}
                  aria-label="quick-troubleshoot"
                >
                  <LockResetIcon sx={{ fontSize: 24 }} />
                </IconButton>
              </div>

              {selectedOrderType === "GST_INVOICE" ? (
                /* ── GST customer details right-panel ── */
                <div className="ccd-gst-rp-ref">
                  {/* ── Loading state ── */}
                  {gstActiveTab === "organization" &&
                    gstSelectedPartner &&
                    gstPartnerDetailsLoading && (
                      <div className="ccd-gst-rp-loading-ref">
                        <CircularProgress size={18} sx={{ color: "#2a71ff" }} />
                        <span>Loading partner details…</span>
                      </div>
                    )}

                  {/* ── Error state ── */}
                  {gstActiveTab === "organization" &&
                    gstSelectedPartner &&
                    gstPartnerDetailsError && (
                      <div className="ccd-gst-detail-error-ref">
                        <p>Could not load details. Confirm is unavailable.</p>
                        <button
                          className="ccd-gst-retry-btn-ref"
                          onClick={handleRetryGstPartnerDetails}
                          disabled={gstPartnerDetailsLoading}
                        >
                          {gstPartnerDetailsLoading ? (
                            <CircularProgress size={12} sx={{ color: "#d97706" }} />
                          ) : (
                            "Retry"
                          )}
                        </button>
                      </div>
                    )}

                  {/* ── Empty state ── */}
                  {!gstPartnerDetailsLoading &&
                    !gstPartnerDetailsError &&
                    (() => {
                      const orgReady =
                        gstActiveTab === "organization" && gstSelectedPartner && gstPartnerDetails;
                      const newReady = gstActiveTab === "new" && isValidGSTINFormat(gstNewGstin);
                      if (orgReady || newReady) return null;
                      return (
                        <div className="ccd-insights-empty-card-ref" style={{ marginTop: "12px" }}>
                          <div className="ccd-insights-empty-icon-ref">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              aria-hidden="true"
                            >
                              <path d="M3 9h18v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9Z" />
                              <path d="m3 9 2.45-4.9A2 2 0 0 1 7.24 3h9.52a2 2 0 0 1 1.8 1.1L21 9" />
                              <path d="M12 3v6" />
                            </svg>
                          </div>
                          <p className="ccd-insights-empty-title-ref">No organisation selected</p>
                          <p className="ccd-insights-empty-sub-ref">
                            {gstActiveTab === "organization"
                              ? "Search and select an existing partner, or switch to New Customer to register with a GSTIN."
                              : "Enter a valid 15-digit GSTIN to proceed."}
                          </p>
                        </div>
                      );
                    })()}

                  {/* ── Business + contact details ── */}
                  {!gstPartnerDetailsLoading &&
                    !gstPartnerDetailsError &&
                    (() => {
                      const orgReady =
                        gstActiveTab === "organization" && gstSelectedPartner && gstPartnerDetails;
                      const newReady = gstActiveTab === "new" && isValidGSTINFormat(gstNewGstin);
                      if (!orgReady && !newReady) return null;

                      const bizName = orgReady
                        ? gstPartnerDetails.displayName ||
                          gstPartnerDetails.customerName ||
                          gstSelectedPartner?.displayName ||
                          "—"
                        : gstValidatedBizName || "New customer";
                      const legalName = orgReady ? "" : gstValidatedLegalName;
                      const gstNum = orgReady ? gstPartnerDetails?.gstNumber : gstNewGstin;
                      const rpStatus = orgReady ? "" : gstValidatedStatus;
                      const rpType = orgReady ? "" : gstValidatedType;
                      const addrParts =
                        orgReady && gstPartnerDetails?.addresses?.[0]
                          ? [
                              gstPartnerDetails.addresses[0].addressLine1,
                              gstPartnerDetails.addresses[0].city,
                              gstPartnerDetails.addresses[0].state,
                              gstPartnerDetails.addresses[0].pincode,
                            ].filter(Boolean)
                          : newReady
                          ? [
                              gstValidatedAddr.addressLine1,
                              gstValidatedAddr.addressLine2,
                              gstValidatedAddr.city,
                              gstValidatedAddr.state,
                              gstValidatedAddr.pincode,
                            ].filter(Boolean)
                          : [];

                      return (
                        <>
                          {/* Business info */}
                          <div className="ccd-gst-rp-biz-card-ref">
                            <p className="ccd-gst-rp-section-label-ref">Business</p>
                            <p className="ccd-gst-rp-biz-name-ref">{bizName}</p>
                            {legalName && legalName !== bizName && (
                              <p className="ccd-gst-rp-legal-name-ref">{legalName}</p>
                            )}
                            <div className="ccd-gst-rp-badge-row-ref">
                              {gstNum && (
                                <span className="ccd-gst-rp-gstin-badge-ref">{gstNum}</span>
                              )}
                              {rpType && <span className="ccd-gst-type-chip-ref">{rpType}</span>}
                              {rpStatus && (
                                <span
                                  className={`ccd-gst-status-chip-ref${
                                    rpStatus.toLowerCase() === "active" ? " active" : " inactive"
                                  }`}
                                >
                                  {rpStatus}
                                </span>
                              )}
                            </div>
                            {addrParts.length > 0 && (
                              <p className="ccd-gst-rp-addr-ref">
                                <svg
                                  width="12"
                                  height="12"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  style={{ flexShrink: 0, marginTop: "1px" }}
                                >
                                  <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
                                  <circle cx="12" cy="10" r="3" />
                                </svg>
                                {addrParts.join(", ")}
                              </p>
                            )}
                          </div>

                          {/* Contact details — editable */}
                          <div className="ccd-gst-rp-contact-ref">
                            <p className="ccd-gst-rp-section-label-ref">Contact details</p>
                            <div className="ccd-gst-rp-field-ref">
                              <label className="ccd-input-label-ref">
                                Mobile number
                                {gstActiveTab === "new" && (
                                  <span style={{ color: "#ef4444", marginLeft: "3px" }}>*</span>
                                )}
                              </label>
                              <FormControl
                                variant="standard"
                                sx={{ width: "100%", marginTop: "6px" }}
                              >
                                <NewCountryCodeInput
                                  countryOptions={countryOptions}
                                  customerMobileNumber={gstContactPhone}
                                  setCustomerMobileNumber={(v) => {
                                    setGstContactPhone(v);
                                    if (gstContactPhoneError) setGstContactPhoneError("");
                                  }}
                                  handleKeyPress={() => {}}
                                  selectedCountry={gstContactCountry}
                                  handleCountryChange={setGstContactCountry}
                                />
                              </FormControl>
                              {gstContactPhoneError && (
                                <p
                                  style={{
                                    fontSize: "12px",
                                    color: "#ef4444",
                                    marginTop: "5px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    style={{ flexShrink: 0 }}
                                  >
                                    <circle cx="12" cy="12" r="10" />
                                    <path d="M12 8v4M12 16h.01" />
                                  </svg>
                                  {gstContactPhoneError}
                                </p>
                              )}
                            </div>
                            <div className="ccd-gst-rp-field-ref" style={{ marginTop: "10px" }}>
                              <label className="ccd-input-label-ref">Contact name</label>
                              <input
                                className="ccd-gst-val-input-ref"
                                style={{
                                  marginTop: "6px",
                                  display: "block",
                                  width: "100%",
                                  boxSizing: "border-box",
                                }}
                                placeholder="Contact person name"
                                value={gstContactName}
                                onChange={(e) => setGstContactName(e.target.value)}
                                data-testid="gst-contact-name-rp"
                              />
                            </div>

                            {/* WhatsApp notification toggle */}
                            <label
                              className="gcd-whatsapp-label gcd-whatsapp-label--gst"
                              htmlFor="gcd-gst-whatsapp-chk"
                            >
                              <div className="gcd-whatsapp-left">
                                <span className="gcd-whatsapp-icon" aria-hidden="true">
                                  <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                  >
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                  </svg>
                                </span>
                                <span className="gcd-whatsapp-text">
                                  Get notification on WhatsApp
                                </span>
                              </div>
                              <div
                                className={`gcd-whatsapp-toggle${
                                  gstWhatsApp ? " gcd-whatsapp-toggle--on" : ""
                                }`}
                              >
                                <div className="gcd-whatsapp-thumb" />
                              </div>
                              <input
                                type="checkbox"
                                id="gcd-gst-whatsapp-chk"
                                checked={gstWhatsApp}
                                onChange={(e) => setGstWhatsApp(e.target.checked)}
                                style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
                              />
                            </label>
                          </div>
                        </>
                      );
                    })()}

                  {/* ── Pricing mode selector ── */}
                  <div className="ccd-gst-pricing-toggle-ref">
                    <p className="ccd-gst-rp-section-label-ref" style={{ marginBottom: "8px" }}>
                      Pricing mode
                    </p>
                    <div className="ccd-gst-pricing-options-ref">
                      <button
                        type="button"
                        className={`ccd-gst-pricing-btn-ref retail${
                          gstPricingMode === "RETAIL" ? " active" : ""
                        }`}
                        onClick={() => setGstPricingMode("RETAIL")}
                        data-testid="gst-pricing-retail"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M12 2H2v10l9.29 9.29a1 1 0 0 0 1.41 0l6.59-6.59a1 1 0 0 0 0-1.41z" />
                          <circle cx="7" cy="7" r="2" />
                        </svg>
                        Retail price
                        {gstPricingMode === "RETAIL" && (
                          <svg
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            style={{ marginLeft: "auto" }}
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </button>
                      <button
                        type="button"
                        className={`ccd-gst-pricing-btn-ref wholesale${
                          gstPricingMode === "WHOLESALE" ? " active" : ""
                        }`}
                        onClick={() => setGstPricingMode("WHOLESALE")}
                        data-testid="gst-pricing-wholesale"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M3 9h18v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9Z" />
                          <path d="m3 9 2.45-4.9A2 2 0 0 1 7.24 3h9.52a2 2 0 0 1 1.8 1.1L21 9" />
                          <path d="M12 3v6" />
                        </svg>
                        Wholesale price
                        {gstPricingMode === "WHOLESALE" && (
                          <svg
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            style={{ marginLeft: "auto" }}
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ) : !isValidMobile ? (
                /* Empty state */
                <>
                  <div className="ccd-insights-empty-card-ref">
                    <div className="ccd-insights-empty-icon-ref">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden="true"
                      >
                        <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                    </div>
                    <p className="ccd-insights-empty-title-ref">Waiting for customer details</p>
                    <p
                      className="ccd-insights-empty-sub-ref"
                      data-testid="text-customer-preview-empty-state"
                    >
                      Add a mobile number and name to continue with the selected order flow.
                    </p>
                  </div>
                  <div className="gcd-sponsor">
                    <span className="gcd-sponsor-label">Sponsored by</span>
                    <img src={AachiLogo} alt="Aachi" className="gcd-sponsor-logo" />
                  </div>
                </>
              ) : (
                /* Customer insights tiles */
                <>
                  <div className="ccd-insights-card-ref">
                    <div className="ccd-insights-card-header-ref">
                      <div>
                        <p
                          className="ccd-insights-card-name-ref"
                          data-testid="text-customer-preview-name"
                        >
                          {name || "—"}
                        </p>
                        <p
                          className="ccd-insights-card-mobile-ref"
                          data-testid="text-customer-preview-mobile"
                        >
                          {selectedCountry?.value} {mobile}
                        </p>
                      </div>
                      <div
                        className="ccd-insights-card-badge-ref"
                        data-testid="status-customer-preview-member-since"
                      >
                        {(() => {
                          const created =
                            foundCustomer?.createdAt ||
                            foundCustomer?.updatedAt ||
                            foundCustomer?.created;
                          return created ? moment(created).format("MMM YYYY") : "...";
                        })()}
                      </div>
                    </div>
                    <div className="ccd-insights-card-option-ref">
                      <p className="ccd-insights-card-option-label-ref">Order option</p>
                      <p
                        className="ccd-insights-card-option-val-ref"
                        data-testid="text-customer-preview-order-option"
                      >
                        {saleModeLabel}
                      </p>
                    </div>
                  </div>

                  <div className="ccd-insights-grid-ref">
                    <div className="ccd-insights-tile-ref">
                      <p className="ccd-insights-tile-label-ref">Total orders</p>
                      <p
                        className="ccd-insights-tile-val-ref"
                        data-testid="text-customer-preview-total-orders"
                      >
                        {tilesLoading ? (
                          <InsightSpinner />
                        ) : (
                          formatInsightCount(insights?.totalOrders)
                        )}
                      </p>
                    </div>
                    <div className="ccd-insights-tile-ref">
                      <p className="ccd-insights-tile-label-ref">Loyalty points</p>
                      <p
                        className="ccd-insights-tile-val-ref"
                        data-testid="text-customer-preview-loyalty-points"
                      >
                        {tilesLoading ? (
                          <InsightSpinner />
                        ) : (
                          formatInsightCount(insights?.loyaltyPoints)
                        )}
                      </p>
                    </div>
                    <div className="ccd-insights-tile-ref">
                      <p className="ccd-insights-tile-label-ref">Wallet Balance</p>
                      <div className="ccd-insights-tile-val-ref">
                        {tilesLoading ? (
                          <InsightSpinner />
                        ) : (
                          <>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="#10B981"
                              strokeWidth="2"
                              aria-hidden="true"
                            >
                              <path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1"></path>
                              <path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4"></path>
                            </svg>
                            <span data-testid="text-customer-preview-wallet-balance">
                              {formatInsightCurrency(insights?.walletBalance)}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="ccd-insights-tile-ref">
                      <p className="ccd-insights-tile-label-ref">Average basket</p>
                      <p
                        className="ccd-insights-tile-val-ref"
                        data-testid="text-customer-preview-average-basket"
                      >
                        {tilesLoading ? (
                          <InsightSpinner />
                        ) : (
                          formatInsightCurrency(insights?.averageBasket)
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="ccd-insights-last-ref">
                    <p className="ccd-insights-last-label-ref">Last ordered</p>
                    <p
                      className="ccd-insights-last-val-ref"
                      data-testid="text-customer-preview-last-ordered"
                    >
                      {tilesLoading ? (
                        <InsightSpinner small />
                      ) : (
                        formatInsightDate(insights?.lastOrderedAt)
                      )}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── Sticky footer ── */}
        <div className="ccd-footer-ref">
          <div className="ccd-footer-inner-ref">
            <button
              className="ccd-btn-skip-ref"
              type="button"
              disabled={skipLoading || addLoading || gstLoading}
              onClick={handleSkip}
              data-testid="button-customer-skip"
            >
              {skipLoading ? "Skipping…" : "Skip"}
            </button>

            {selectedOrderType === "GST_INVOICE" ? (
              <button
                className={`ccd-btn-add-ref ccd-btn-b2b-ref${
                  !canCreateB2BOrder ? " visual-disabled" : ""
                }`}
                type="button"
                disabled={!canCreateB2BOrder || gstLoading}
                onClick={handleCreateB2BOrder}
                data-testid="button-create-b2b-order"
              >
                {gstLoading ? (
                  <>
                    <CircularProgress size={14} sx={{ color: "#fff", marginRight: "6px" }} />
                    {gstActiveTab === "organization" ? "Going to cart…" : "Adding…"}
                  </>
                ) : (
                  <>
                    {gstActiveTab === "organization" ? (
                      /* Go to cart — shopping cart icon */
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        style={{ marginRight: "6px" }}
                      >
                        <circle cx="8" cy="21" r="1" />
                        <circle cx="19" cy="21" r="1" />
                        <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" />
                      </svg>
                    ) : (
                      /* Add customer — user-plus icon */
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        style={{ marginRight: "6px" }}
                      >
                        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                        <circle cx="9" cy="7" r="4" />
                        <line x1="19" y1="8" x2="19" y2="14" />
                        <line x1="22" y1="11" x2="16" y2="11" />
                      </svg>
                    )}
                    {gstActiveTab === "organization" ? "Go to cart" : "Add customer"}
                  </>
                )}
              </button>
            ) : (
              <button
                className={`ccd-btn-add-ref${!canAdd ? " visual-disabled" : ""}`}
                type="button"
                disabled={!canAdd || addLoading || skipLoading}
                onClick={handleAdd}
                data-testid="button-customer-add"
              >
                {addLoading ? "Adding..." : "Add Customer"}
              </button>
            )}
          </div>
        </div>
      </Box>
    </Modal>
  );
};

export default GroceryAddCustomerModal;
